import React, { useEffect, useRef, useState } from 'react';
import planck from 'planck-js';

// Modified hook to safely handle server-side rendering
function useVisualViewport() {
  // Initialize with default values
  const [viewport, setViewport] = useState({
    width: 1200, // Default fallback width
    height: 800, // Default fallback height
  });

  useEffect(() => {
    // Only access window in useEffect (client-side only)
    if (typeof window !== 'undefined') {
      // Update with actual values once in browser
      setViewport({
        width: window.visualViewport ? window.visualViewport.width : window.innerWidth,
        height: window.visualViewport ? window.visualViewport.height : window.innerHeight,
      });

      const handleResize = () => {
        setViewport({
          width: window.visualViewport ? window.visualViewport.width : window.innerWidth,
          height: window.visualViewport ? window.visualViewport.height : window.innerHeight,
        });
      };

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
        window.visualViewport.addEventListener('scroll', handleResize);
        return () => {
          window.visualViewport.removeEventListener('resize', handleResize);
          window.visualViewport.removeEventListener('scroll', handleResize);
        };
      } else {
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
        };
      }
    }
  }, []);

  return viewport;
}

// Array of colors
const colorOptions = [
  '#B9EBC4', // Light Mint
  '#A8D5C3', // Light Aquamarine
  '#C2E8D1', // Mint Ice
  '#E3F7DF', // Creamy Green
  '#D9F4E0', // Pastel Green
  '#C8E9D9', // Soft Aquamarine Green
  '#EAF9EB', // Light Pistachio
  '#D0F0E0', // Pastel Olive
  '#F0F9EE', // Barely Visible Green
  '#D4F7EC', // Watercolor Mint
];

// Array of icon slugs for balls
const iconSlugs = [
  'basic-augmented-reality-virtual-reality-ar',
  'basic-handshake-agreement-partnership-icon',
  'basic-money-transfer-finance-icon',
  'basic-move-left-arrow-back-arrow-icon',
  'basic-car-charging-automotive-battery-icon',
  'basic-expand-enlarge-focus-icon',
  'basic-small-arrow-right-arrow-right-icon',
  'basic-remove-user-group-group-of-users-delete-group-icon',
  'basic-delete-user-remove-profile-account-cancellation-icon',
  'basic-bookmark-save-ribbon-icon',
  'basic-briefcase-business-professional-icon',
  'basic-credit-card-payment-payment-type-icon',
  'basic-frame-capture-blank-icon',
  'basic-network-folder-folder-connectivity-icon',
  'basic-edit-image-adjust-compose-icon',
  'basic-shining-stars-bright',
  'basic-information-help-details-icon',
  'basic-user-person-avatar-icon',
  'basic-alliance-association-collaboration-icon',
  'basic-eye-closed-blind',
  'basic-user-person-profile-icon',
  'basic-group-team-users-icon',
];

const PlanckScene = () => {
  const canvasRef = useRef(null); // Reference to canvas element
  const worldRef = useRef(null); // Reference to Planck.js world
  const ballsRef = useRef([]); // Array of balls
  const imagesRef = useRef({}); // Preloaded images
  const animationFrameIdRef = useRef(null); // Animation frame ID
  const [isClient, setIsClient] = useState(false); // Flag to check if we're on client-side
  const lastTimeRef = useRef(0); // Reference for tracking time between frames

  // Use custom hook to get visual viewport dimensions
  const { width: windowWidth, height: windowHeight } = useVisualViewport();

  // Determine if device is mobile
  const isMobile = windowWidth < 600;

  // Define scaling factor based on device type
  const scaleFactor = isMobile ? 0.70 : 1;

  // Define canvas dimensions based on viewport with max height of 900px
  const canvasDimensions = {
    width: windowWidth,
    height: Math.min(windowHeight, 900), // Limit max height to 900px
  };

  // Define number of balls based on viewport width
  const numberOfBalls = windowWidth < 600 ? 7 : windowWidth < 1200 ? 10 : 15;

  // Set isClient to true after component mounts (client-side only)
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Initialize Planck.js world and handle animation
  useEffect(() => {
    // Skip if not on client-side
    if (!isClient) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

    // Set canvas dimensions considering devicePixelRatio
    canvas.width = canvasDimensions.width * dpr;
    canvas.height = canvasDimensions.height * dpr;
    canvas.style.width = `${canvasDimensions.width}px`;
    canvas.style.height = `${canvasDimensions.height}px`;
    context.scale(dpr, dpr);

    // Disable smoothing for sharper images
    context.imageSmoothingEnabled = false;

    if (canvasDimensions.width === 0 || canvasDimensions.height === 0) return;

    const SCALE = 80;

    // Initialize world only once
    if (!worldRef.current) {
      const world = planck.World({
        gravity: planck.Vec2(0, 5), // Reduced gravity for calmer simulation
      });

      // Function to create static walls with 3% thickness
      const createWalls = () => {
        const wallThicknessX = (0.03 * canvasDimensions.width) / SCALE; // 3% of width in world units
        const wallThicknessY = (0.03 * canvasDimensions.height) / SCALE; // 3% of height in world units

        // Floor as a thick box
        const groundHeight = wallThicknessY; // 3% of height
        const ground = world.createBody({
          position: planck.Vec2(canvasDimensions.width / 2 / SCALE, (canvasDimensions.height / SCALE) - groundHeight / 2),
        });
        ground.createFixture(planck.Box(canvasDimensions.width / 2 / SCALE, groundHeight / 2), {
          restitution: 0.6, // Reduced bounce for more stable rebounds
          friction: 0.5,
        });

        // Left wall
        const leftWall = world.createBody({
          position: planck.Vec2(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE),
        });
        leftWall.createFixture(planck.Box(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE), {
          restitution: 0.7, // Reduced from 1.0 to 0.7
          friction: 0.5,
        });

        // Right wall
        const rightWall = world.createBody({
          position: planck.Vec2((canvasDimensions.width / SCALE) - wallThicknessX / 2, canvasDimensions.height / 2 / SCALE),
        });
        rightWall.createFixture(planck.Box(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE), {
          restitution: 0.7, // Reduced from 1.0 to 0.7
          friction: 0.5,
        });

        // Top wall (optional, if needed to limit the top area)
        const topWall = world.createBody({
          position: planck.Vec2(canvasDimensions.width / 2 / SCALE, wallThicknessY / 2),
        });
        topWall.createFixture(planck.Box(canvasDimensions.width / 2 / SCALE, wallThicknessY / 2), {
          restitution: 0.7, // Reduced from 1.0 to 0.7
          friction: 0.5,
        });
      };

      createWalls();

      // Create balls with random properties
      const createdBalls = [];
      const baseRadiusOriginal = 90 / SCALE; // Base radius 90/SCALE units (~1.125 units)

      // Apply scaling factor to base radius
      const baseRadius = baseRadiusOriginal * scaleFactor;

      // Shuffle iconSlugs array for random icon selection
      const shuffledIconSlugs = [...iconSlugs].sort(() => 0.5 - Math.random());

      // Limit the number of balls based on numberOfBalls and available icons
      const selectedIconSlugs = shuffledIconSlugs.slice(0, Math.min(numberOfBalls, iconSlugs.length));

      selectedIconSlugs.forEach((slug, index) => {
        const radiusVariation = 0.4;
        const radius = Math.max(
          baseRadius * (1 + Math.random() * radiusVariation - radiusVariation / 2),
          baseRadius * 0.6 // Minimum radius 60% of base
        );

        const x = Math.random() * (canvasDimensions.width / SCALE - 2) + 1; // Ensure balls are inside walls
        const y = Math.random() * (canvasDimensions.height / SCALE - 5) + 1; // Initial position slightly above floor

        const initialVelocity = planck.Vec2(
          (Math.random() - 0.5) * 8, // Reduced initial velocity from 15 to 8
          (Math.random() - 0.5) * 8
        );

        const ballBody = world.createDynamicBody({
          position: planck.Vec2(x, y),
          linearVelocity: initialVelocity,
        });
        
        ballBody.createFixture(planck.Circle(radius), {
          restitution: 0.6, // Reduced bounce from 0.7 to 0.6
          friction: 0.5,
          density: 0.1,
        });
        
        // Add linear damping for smooth deceleration
        ballBody.setLinearDamping(0.2);
        
        ballBody.iconSlug = slug;

        const color = colorOptions[Math.floor(Math.random() * colorOptions.length)];

        createdBalls.push({
          id: index,
          body: ballBody,
          radius,
          iconSlug: slug,
          color,
        });
      });

      ballsRef.current = createdBalls;
      worldRef.current = world;

      // Preload images
      const images = {};
      selectedIconSlugs.forEach((slug) => {
        const img = new Image();
        img.src = `https://icons-pbl.davooda.com/basic-icons/${slug}.svg`;
        images[slug] = img;
      });
      imagesRef.current = images;
    } else {
      // Update walls if viewport size changed
      const world = worldRef.current;

      // Remove old static bodies (walls)
      const bodiesToRemove = [];

      for (let body = world.getBodyList(); body; body = body.getNext()) {
        if (body.isStatic()) {
          bodiesToRemove.push(body);
        }
      }

      bodiesToRemove.forEach((body) => {
        world.destroyBody(body);
      });

      // Recreate walls with new dimensions
      const createWalls = () => {
        const wallThicknessX = (0.03 * canvasDimensions.width) / SCALE; // 3% of width in world units
        const wallThicknessY = (0.03 * canvasDimensions.height) / SCALE; // 3% of height in world units

        // Floor as a thick box
        const groundHeight = wallThicknessY; // 3% of height
        const ground = world.createBody({
          position: planck.Vec2(canvasDimensions.width / 2 / SCALE, (canvasDimensions.height / SCALE) - groundHeight / 2),
        });
        ground.createFixture(planck.Box(canvasDimensions.width / 2 / SCALE, groundHeight / 2), {
          restitution: 0.5,
          friction: 0.5,
        });

        // Left wall
        const leftWall = world.createBody({
          position: planck.Vec2(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE),
        });
        leftWall.createFixture(planck.Box(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE), {
          restitution: 0.7,
          friction: 0.5,
        });

        // Right wall
        const rightWall = world.createBody({
          position: planck.Vec2((canvasDimensions.width / SCALE) - wallThicknessX / 2, canvasDimensions.height / 2 / SCALE),
        });
        rightWall.createFixture(planck.Box(wallThicknessX / 2, canvasDimensions.height / 2 / SCALE), {
          restitution: 0.7,
          friction: 0.5,
        });

        // Top wall (optional)
        const topWall = world.createBody({
          position: planck.Vec2(canvasDimensions.width / 2 / SCALE, wallThicknessY / 2),
        });
        topWall.createFixture(planck.Box(canvasDimensions.width / 2 / SCALE, wallThicknessY / 2), {
          restitution: 0.7,
          friction: 0.5,
        });
      };

      createWalls();
    }

    const world = worldRef.current;
    const contextCanvas = canvasRef.current.getContext('2d');

    // Function for rendering the scene
    const renderScene = () => {
      contextCanvas.clearRect(0, 0, canvasDimensions.width, canvasDimensions.height);

      // Draw each ball
      ballsRef.current.forEach((ball) => {
        const position = ball.body.getPosition();
        const x = position.x * SCALE;
        const y = position.y * SCALE;

        // Draw ball circle
        contextCanvas.beginPath();
        contextCanvas.arc(x, y, ball.radius * SCALE, 0, 2 * Math.PI);
        contextCanvas.fillStyle = ball.color;
        contextCanvas.fill();
        contextCanvas.closePath();

        // Draw icon
        const img = imagesRef.current[ball.iconSlug];
        if (img.complete) {
          // Adjust icon size with scaleFactor
          const iconSize = 40 * scaleFactor;
          contextCanvas.drawImage(img, x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);
        } else {
          img.onload = () => {
            const iconSize = 40 * scaleFactor;
            contextCanvas.drawImage(img, x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);
          };
        }
      });
    };

    // Improved simulation parameters for stable physics
    const fixedTimeStep = 1 / 60; // 60 frames per second
    const velocityIterations = 8;
    const positionIterations = 8;
    const maxSubSteps = 5; // Prevents "spiral of death" during long pauses

    // Improved animation loop with time management
    const animate = (time) => {
      if (lastTimeRef.current) {
        // Convert milliseconds to seconds for planck
        const deltaTime = Math.min((time - lastTimeRef.current) / 1000, 0.1); // Limit to 0.1 sec
        
        // Physics step considering real time
        world.step(fixedTimeStep, velocityIterations, positionIterations, maxSubSteps, deltaTime);
      }
      
      lastTimeRef.current = time;
      renderScene();
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);

    // Handle user interactions (dragging balls)
    let isDragging = false;
    let mouseJoint = null;

    // Mouse event handlers
    const handleMouseDown = (event) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const mouseX = (event.clientX - rect.left) / SCALE;
      const mouseY = (event.clientY - rect.top) / SCALE;

      // Find ball under cursor
      const hit = ballsRef.current.find((ball) => {
        const pos = ball.body.getPosition();
        const dx = mouseX - pos.x;
        const dy = mouseY - pos.y;
        return Math.sqrt(dx * dx + dy * dy) < ball.radius;
      });

      if (hit) {
        isDragging = true;

        // Create Mouse Joint
        mouseJoint = world.createJoint(
          planck.MouseJoint({
            maxForce: 1000 * hit.body.getMass(), // Reduced from 1500 to 1000
            target: planck.Vec2(mouseX, mouseY),
            bodyA: world.createBody(),
            bodyB: hit.body,
          })
        );

        event.preventDefault();
      }
    };

    const handleMouseMove = (event) => {
      if (isDragging && mouseJoint) {
        const rect = canvasRef.current.getBoundingClientRect();
        const mouseX = (event.clientX - rect.left) / SCALE;
        const mouseY = (event.clientY - rect.top) / SCALE;
        mouseJoint.setTarget(planck.Vec2(mouseX, mouseY));
        event.preventDefault();
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        isDragging = false;
        if (mouseJoint) {
          world.destroyJoint(mouseJoint);
          mouseJoint = null;
        }
      }
    };

    // Touch event handlers
    const handleTouchStart = (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      const rect = canvasRef.current.getBoundingClientRect();
      const touchX = (touch.clientX - rect.left) / SCALE;
      const touchY = (touch.clientY - rect.top) / SCALE;

      // Find ball under touch
      const hit = ballsRef.current.find((ball) => {
        const pos = ball.body.getPosition();
        const dx = touchX - pos.x;
        const dy = touchY - pos.y;
        return Math.sqrt(dx * dx + dy * dy) < ball.radius;
      });

      if (hit) {
        isDragging = true;

        // Create Mouse Joint
        mouseJoint = world.createJoint(
          planck.MouseJoint({
            maxForce: 1000 * hit.body.getMass(), // Reduced from 1500 to 1000
            target: planck.Vec2(touchX, touchY),
            bodyA: world.createBody(),
            bodyB: hit.body,
          })
        );

        // Prevent default touch behavior (e.g., scrolling)
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      if (isDragging && mouseJoint) {
        const touch = event.touches[0];
        if (!touch) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const touchX = (touch.clientX - rect.left) / SCALE;
        const touchY = (touch.clientY - rect.top) / SCALE;
        mouseJoint.setTarget(planck.Vec2(touchX, touchY));

        // Prevent default touch behavior (e.g., scrolling)
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      if (isDragging) {
        isDragging = false;
        if (mouseJoint) {
          world.destroyJoint(mouseJoint);
          mouseJoint = null;
        }
      }
    };

    // Add event handlers
    if (canvasRef.current) {
      // Mouse events
      canvasRef.current.addEventListener('mousedown', handleMouseDown);
      canvasRef.current.addEventListener('mousemove', handleMouseMove);
      canvasRef.current.addEventListener('mouseup', handleMouseUp);
      canvasRef.current.addEventListener('mouseleave', handleMouseUp);

      // Touch events
      canvasRef.current.addEventListener('touchstart', handleTouchStart, { passive: false });
      canvasRef.current.addEventListener('touchmove', handleTouchMove, { passive: false });
      canvasRef.current.addEventListener('touchend', handleTouchEnd);
      canvasRef.current.addEventListener('touchcancel', handleTouchEnd);
    }

    // Cleanup on component unmount
    return () => {
      cancelAnimationFrame(animationFrameIdRef.current);

      if (canvasRef.current) {
        // Remove mouse event handlers
        canvasRef.current.removeEventListener('mousedown', handleMouseDown);
        canvasRef.current.removeEventListener('mousemove', handleMouseMove);
        canvasRef.current.removeEventListener('mouseup', handleMouseUp);
        canvasRef.current.removeEventListener('mouseleave', handleMouseUp);

        // Remove touch event handlers
        canvasRef.current.removeEventListener('touchstart', handleTouchStart);
        canvasRef.current.removeEventListener('touchmove', handleTouchMove);
        canvasRef.current.removeEventListener('touchend', handleTouchEnd);
        canvasRef.current.removeEventListener('touchcancel', handleTouchEnd);
      }
    };
  }, [canvasDimensions.width, canvasDimensions.height, numberOfBalls, scaleFactor, isMobile, isClient]);

  // Return a loading state or render the component
  if (!isClient) {
    return (
      <div className="relative w-full h-screen">
        <div className="absolute">
          {/* Loading state */}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ maxHeight: '900px' }}>
      {/* Overlay content */}
      <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center z-10 w-3/5 pointer-events-none">
        <h1 className="text-5xl leading-tight tracking-tight m-0 mb-2 md:text-3xl">Explore our icons collection</h1>
        <p className="text-base text-black m-0 mt-2">
          Outline pixel perfect premium icons and free 3D icons are our largest collection
        </p>
      </div>

      {/* Planck.js canvas */}
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full touch-pan-y touch-pinch-zoom"
        // Width and height are set in useEffect considering dpr
      ></canvas>
    </div>
  );
};

export default PlanckScene;